import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Camera, ScanLine, MessageCircle, Star, MessageSquareText, Phone, Link2, ArrowRight } from "lucide-react";

/**
 * Web replica of the printable QR Kit posters from the GrabNChat app
 * (grabnchat-new-user-app › QrKitScreen). Same 360 × 509 layout (A-series
 * 1 : 1.414 ratio), same four colour styles and the same copy, so what a
 * vendor sees on the website is what they print from the app.
 */

export const KIT_THEMES = [
  { key: "brand", label: "Brand", primary: "#F97316", secondary: "#FB923C", background: "#FFF7ED", text: "#1F2937" },
  { key: "whatsapp", label: "WhatsApp", primary: "#128C7E", secondary: "#25D366", background: "#F0FDF4", text: "#1F2937" },
  { key: "navy", label: "Navy", primary: "#1A3166", secondary: "#3B82F6", background: "#EFF6FF", text: "#111827" },
  { key: "dark", label: "Dark", primary: "#111827", secondary: "#F59E0B", background: "#F3F4F6", text: "#111827" },
];

const WHATSAPP_GREEN = "#25D366";
const STAR_GOLD = "#F59E0B";
export const POSTER_W = 360;
export const POSTER_H = 509;

export function WhatsAppGlyph({ className = "", style }) {
  return (
    <svg viewBox="0 0 24 24" className={className} style={style} fill="currentColor" aria-hidden="true">
      <path d="M12.004 2.003c-5.514 0-9.997 4.483-9.997 9.997 0 1.762.463 3.483 1.343 4.997L2 22l5.1-1.338a9.958 9.958 0 004.905 1.286h.004c5.514 0 9.997-4.483 9.997-9.997 0-2.67-1.04-5.18-2.929-7.07a9.935 9.935 0 00-7.07-2.878zm.001 18.174h-.003a8.13 8.13 0 01-4.15-1.135l-.297-.176-3.028.794.808-2.951-.194-.303a8.128 8.128 0 01-1.246-4.346c0-4.494 3.658-8.15 8.153-8.15 2.178 0 4.225.849 5.764 2.39a8.108 8.108 0 012.386 5.764c0 4.494-3.657 8.113-8.193 8.113z" />
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
    </svg>
  );
}

function initials(name) {
  const words = (name || "").split(/\s+/).filter((w) => w && /[A-Za-z0-9]/.test(w[0]));
  return (words.slice(0, 2).map((w) => w[0]).join("") || "G").toUpperCase();
}

function PosterFrame({ t, businessName, logoSrc, children }) {
  return (
    <div style={{ width: POSTER_W, height: POSTER_H, background: "#fff", display: "flex", flexDirection: "column", fontFamily: "Inter, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
      <div style={{ padding: "22px 20px 20px", background: `linear-gradient(135deg, ${t.primary}, ${t.secondary})`, display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 46, height: 46, borderRadius: 12, background: "#fff", display: "grid", placeItems: "center", overflow: "hidden", flexShrink: 0, boxShadow: "0 4px 12px rgba(0,0,0,.12)" }}>
          {logoSrc ? (
            <img src={logoSrc} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <span style={{ color: t.primary, fontWeight: 800, fontSize: 18 }}>{initials(businessName)}</span>
          )}
        </div>
        <div style={{ color: "#fff", fontSize: 19, fontWeight: 800, lineHeight: 1.15, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", wordBreak: "break-word" }}>
          {businessName?.trim() || "Our Business"}
        </div>
      </div>
      <div style={{ flex: 1, background: t.background, padding: "16px 22px 12px", display: "flex", flexDirection: "column", alignItems: "center", minHeight: 0 }}>
        {children}
      </div>
      <div style={{ background: t.primary, color: "#fff", textAlign: "center", fontSize: 10, fontWeight: 600, letterSpacing: ".4px", padding: "7px 0" }}>
        Powered by GrabnChat
      </div>
    </div>
  );
}

function Headline({ t, text, icon }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: t.text, fontSize: 18, fontWeight: 800, textAlign: "center", lineHeight: 1.2 }}>
      {icon}
      <span style={{ wordBreak: "break-word" }}>{text}</span>
    </div>
  );
}

function Subline({ t, text }) {
  return <div style={{ color: t.text, opacity: 0.65, fontSize: 11.5, textAlign: "center" }}>{text}</div>;
}

function QrCard({ t, value, size, badgeColor, badge }) {
  return (
    <div style={{ background: "#fff", borderRadius: 18, padding: 12, border: `2px solid ${t.primary}22`, boxShadow: "0 10px 24px -10px rgba(0,0,0,.25)", position: "relative" }}>
      <QRCodeSVG value={value || " "} size={size} level="H" fgColor="#111827" bgColor="#ffffff" marginSize={0} />
      <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 38, height: 38, borderRadius: 999, background: badgeColor, border: "3px solid #fff", display: "grid", placeItems: "center", color: "#fff" }}>
        {badge}
      </div>
    </div>
  );
}

function Pill({ t, icon: Icon, text }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#fff", border: `1px solid ${t.primary}33`, borderRadius: 999, padding: "5px 12px", color: t.text, fontSize: 12.5, fontWeight: 700, maxWidth: "100%" }}>
      <Icon size={13} color={t.primary} strokeWidth={2.4} />
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{text}</span>
    </div>
  );
}

function Steps({ t, steps }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "center", gap: 6, width: "100%" }}>
      {steps.map(([Icon, label], i) => (
        <div key={label} style={{ display: "contents" }}>
          {i > 0 && <ArrowRight size={14} color={t.primary} style={{ opacity: 0.6, marginTop: 11 }} />}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 74 }}>
            <div style={{ width: 36, height: 36, borderRadius: 999, background: `${t.primary}1F`, display: "grid", placeItems: "center" }}>
              <Icon size={18} color={t.primary} />
            </div>
            <div style={{ marginTop: 5, fontSize: 10.5, fontWeight: 600, color: t.text, textAlign: "center", lineHeight: 1.2, whiteSpace: "pre-line" }}>{label}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Fixed-size (360 × 509) poster. Wrap in <ScaledPoster> to fit any width. */
export function QrKitPoster({ kit = "whatsapp", theme = KIT_THEMES[0], businessName, logoSrc, headline, qrValue, pillText }) {
  const t = theme;
  if (kit === "rating") {
    return (
      <PosterFrame t={t} businessName={businessName} logoSrc={logoSrc}>
        <Headline t={t} text={headline?.trim() || "How did we do?"} />
        <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
          {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={22} color={STAR_GOLD} fill={STAR_GOLD} />)}
        </div>
        <div style={{ marginTop: 2 }}><Subline t={t} text="Scan to rate us — it takes 10 seconds" /></div>
        <div style={{ marginTop: 10 }}>
          <QrCard t={t} value={qrValue} size={144} badgeColor={STAR_GOLD} badge={<Star size={18} fill="#fff" color="#fff" />} />
        </div>
        <div style={{ marginTop: 10 }}><Pill t={t} icon={Link2} text={pillText} /></div>
        <div style={{ flex: 1 }} />
        <Steps t={t} steps={[[ScanLine, "Scan\nQR code"], [Star, "Tap\nthe stars"], [MessageSquareText, "Share\nfeedback"]]} />
      </PosterFrame>
    );
  }
  return (
    <PosterFrame t={t} businessName={businessName} logoSrc={logoSrc}>
      <Headline t={t} text={headline?.trim() || "Chat with us on WhatsApp"} icon={<WhatsAppGlyph style={{ width: 20, height: 20, color: WHATSAPP_GREEN, flexShrink: 0 }} />} />
      <div style={{ marginTop: 4 }}><Subline t={t} text="Scan the QR code with your phone camera" /></div>
      <div style={{ marginTop: 14 }}>
        <QrCard t={t} value={qrValue} size={160} badgeColor={WHATSAPP_GREEN} badge={<WhatsAppGlyph style={{ width: 20, height: 20 }} />} />
      </div>
      <div style={{ marginTop: 12 }}><Pill t={t} icon={Phone} text={pillText} /></div>
      <div style={{ flex: 1 }} />
      <Steps t={t} steps={[[Camera, "Open\ncamera"], [ScanLine, "Scan\nQR code"], [MessageCircle, "Start\nchatting"]]} />
    </PosterFrame>
  );
}

/** Scales the fixed-size poster down to its container's width. */
export function ScaledPoster({ maxWidth = POSTER_W, className = "", ...posterProps }) {
  const ref = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setScale(Math.min(1, el.clientWidth / POSTER_W));
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} className={className} style={{ width: "100%", maxWidth }}>
      <div style={{ width: POSTER_W * scale, height: POSTER_H * scale, overflow: "hidden", borderRadius: 10, boxShadow: "0 24px 48px -20px rgba(15,23,42,.35)" }}>
        <div style={{ width: POSTER_W, height: POSTER_H, transform: `scale(${scale})`, transformOrigin: "top left" }}>
          <QrKitPoster {...posterProps} />
        </div>
      </div>
    </div>
  );
}
